// Client-side KYC image preprocessing + capture-quality feedback.
// OCR recommendations R1 (client-side capture feedback) + R2 (auto-crop/deskew).
//
// Runs entirely in the browser BEFORE upload so the user gets specific retake
// guidance without a server round-trip or GPT-4o cost (R1), and the image
// GPT-4o receives is auto-oriented (EXIF), auto-cropped to the document,
// deskewed, and right-sized (R2). The backend (Batch 2) already runs a
// sharp-based quality pre-check; this mirrors it on the client so a clearly
// bad photo is caught at capture time.
//
// Pure numeric/decision helpers are exported for vitest. Canvas-dependent
// helpers are integration-only (no jsdom in the test runner).

export interface KycImageMetrics {
  width: number;
  height: number;
  blurScore: number;   // Laplacian variance; higher = sharper
  brightness: number;  // 0-255 mean luminance
  contrast: number;    // grayscale stddev
  aspectRatio: number; // width / height
}

export interface KycQualityDecision {
  ok: boolean;          // false = block upload with blockReason
  warnings: string[];   // borderline issues the user may proceed past
  blockReason?: string;  // specific retake message when ok === false
}

export interface KycPreprocessResult {
  blob: Blob | null;     // processed JPEG (auto-oriented, cropped, deskewed, right-sized); null when quality.ok === false
  previewUrl: string;   // object URL for the processed blob ('' when blocked)
  width: number;
  height: number;
  metrics: KycImageMetrics;
  quality: KycQualityDecision;
  cropped: boolean;      // whether auto-crop trimmed borders
  deskewed: boolean;     // whether deskew applied a non-zero rotation
  deskewAngle: number;   // degrees applied (0 = none)
}

// Tunable thresholds. Module constants (no env on the client); calibrate post-deploy.
export const KYC_CLIENT_MAX_LONG_EDGE = 2000;
export const KYC_CLIENT_JPEG_QUALITY = 0.85;
export const KYC_CLIENT_BLUR_THRESHOLD = 100;     // Laplacian variance below this = blurry
export const KYC_CLIENT_BLUR_BLOCK = 40;          // below this = block (very blurry)
export const KYC_CLIENT_BRIGHTNESS_LOW = 60;      // below = too dark
export const KYC_CLIENT_BRIGHTNESS_HIGH = 238;    // above = likely washed out / glare
export const KYC_CLIENT_CONTRAST_LOW = 25;        // stddev below this = low contrast
export const KYC_CLIENT_CONTRAST_BLOCK = 12;      // below this = block (flat / no detail)
export const KYC_CLIENT_MIN_ASPECT = 0.5;         // w/h below = too narrow
export const KYC_CLIENT_MAX_ASPECT = 2.2;         // w/h above = too wide
export const KYC_CLIENT_INK_THRESHOLD = 22;       // |lum - bg| above = "ink" (auto-crop)
export const KYC_CLIENT_CROP_MARGIN_RATIO = 0.02;  // margin around ink bbox as fraction of dim
export const KYC_CLIENT_CROP_MAX_TRIM_RATIO = 0.30; // never trim more than 30% from a side
export const KYC_CLIENT_CROP_MIN_AREA_RATIO = 0.30; // skip crop if bbox < 30% of image area
export const KYC_CLIENT_DESKEW_MAX_ANGLE = 3;      // ± degrees to search
export const KYC_CLIENT_DESKEW_STEP = 1;           // degree step
export const KYC_CLIENT_DESKEW_MIN_IMPROVEMENT = 0.10; // require 10% variance improvement
export const KYC_CLIENT_DESKEW_ANALYSIS_MAX_EDGE = 400; // deskew analysis on a small thumb

// HOTFIX 2026-07-21: auto-crop (ink-mask bbox) is misidentifying document
// boundaries on real uploads and cropping to a corner of the ID (user 307),
// cutting off vital fields. Deskew depends on the crop and can also introduce
// tilt. Both geometry transforms are DISABLED until the crop logic is fixed and
// revalidated. EXIF auto-orient + R1 quality gate + downscale + compress stay on.
export const KYC_CLIENT_AUTOCROP_ENABLED = false;
export const KYC_CLIENT_DESKEW_ENABLED = false;

// Convert an RGBA byte array (length = w*h*4) to a grayscale Uint8Array (length = w*h).
// Rec.601 luma. Pure.
export function toGrayscaleArray(
  rgba: Uint8ClampedArray | number[],
  width: number,
  height: number,
): Uint8Array {
  const gray = new Uint8Array(width * height);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
    gray[j] = (r * 0.299 + g * 0.587 + b * 0.114) | 0;
  }
  return gray;
}

// Laplacian [0,1,0; 1,-4,1; 0,1,0] variance → blur score. Higher = sharper.
// Pure. Accepts a grayscale array + dims.
export function computeLaplacianVariance(
  gray: Uint8Array | number[],
  width: number,
  height: number,
): number {
  if (width < 3 || height < 3) return 0;
  const lap = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const v = gray[i - width] + gray[i + width] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      lap[i] = v;
    }
  }
  // Variance of the Laplacian (interior pixels only).
  let sum = 0, sumSq = 0, n = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const v = lap[y * width + x];
      sum += v;
      sumSq += v * v;
      n++;
    }
  }
  if (n === 0) return 0;
  const mean = sum / n;
  return Math.max(0, sumSq / n - mean * mean);
}

// Mean (brightness) + stddev (contrast) of a grayscale array. Pure.
export function computeBrightnessContrast(
  gray: Uint8Array | number[],
): { brightness: number; contrast: number } {
  if (!gray.length) return { brightness: 0, contrast: 0 };
  let sum = 0;
  for (let i = 0; i < gray.length; i++) sum += gray[i];
  const mean = sum / gray.length;
  let acc = 0;
  for (let i = 0; i < gray.length; i++) {
    const d = gray[i] - mean;
    acc += d * d;
  }
  return { brightness: mean, contrast: Math.sqrt(acc / gray.length) };
}

// Build an "ink" mask: a pixel is ink when its luminance differs from the
// background estimate (mean of the border pixels) by more than inkThreshold.
// Returns a Uint8Array of 0/1. Pure.
export function computeInkMask(
  gray: Uint8Array | number[],
  width: number,
  height: number,
  inkThreshold: number,
): { mask: Uint8Array; bgMean: number } {
  const mask = new Uint8Array(width * height);
  // Background estimate: mean of the border (top + bottom rows, left + right cols).
  let bgSum = 0, bgN = 0;
  const border = Math.max(2, Math.floor(Math.min(width, height) * 0.04));
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < border; y++) { bgSum += gray[y * width + x]; bgN++; }
    for (let y = height - border; y < height; y++) { bgSum += gray[y * width + x]; bgN++; }
  }
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < border; x++) { bgSum += gray[y * width + x]; bgN++; }
    for (let x = width - border; x < width; x++) { bgSum += gray[y * width + x]; bgN++; }
  }
  const bgMean = bgN ? bgSum / bgN : 0;
  for (let i = 0; i < gray.length; i++) {
    mask[i] = Math.abs(gray[i] - bgMean) > inkThreshold ? 1 : 0;
  }
  return { mask, bgMean };
}

// Bounding box of ink pixels + margin. Returns null when there is no ink or the
// bbox is too small (< minAreaRatio of the image). Pure.
export function computeBoundingBox(
  mask: Uint8Array | number[],
  width: number,
  height: number,
  marginPx: number,
  minAreaRatio: number,
): { left: number; top: number; right: number; bottom: number } | null {
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (mask[y * width + x]) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null; // no ink
  const left = Math.max(0, minX - marginPx);
  const top = Math.max(0, minY - marginPx);
  const right = Math.min(width, maxX + marginPx);
  const bottom = Math.min(height, maxY + marginPx);
  const bw = right - left;
  const bh = bottom - top;
  if (bw <= 0 || bh <= 0) return null;
  if (bw * bh < width * height * minAreaRatio) return null; // likely a false detection
  return { left, top, right, bottom };
}

// R1 decision: turn metrics into a quality verdict with specific retake guidance.
// Pure + deterministic. `opts` lets tests override thresholds.
export function decideQualityFromMetrics(
  m: KycImageMetrics,
  opts: {
    blurBlock?: number; blurThreshold?: number;
    brightnessLow?: number; brightnessHigh?: number;
    contrastBlock?: number; contrastLow?: number;
    minAspect?: number; maxAspect?: number;
  } = {},
): KycQualityDecision {
  const blurBlock = opts.blurBlock ?? KYC_CLIENT_BLUR_BLOCK;
  const blurThreshold = opts.blurThreshold ?? KYC_CLIENT_BLUR_THRESHOLD;
  const brightnessLow = opts.brightnessLow ?? KYC_CLIENT_BRIGHTNESS_LOW;
  const brightnessHigh = opts.brightnessHigh ?? KYC_CLIENT_BRIGHTNESS_HIGH;
  const contrastBlock = opts.contrastBlock ?? KYC_CLIENT_CONTRAST_BLOCK;
  const contrastLow = opts.contrastLow ?? KYC_CLIENT_CONTRAST_LOW;
  const minAspect = opts.minAspect ?? KYC_CLIENT_MIN_ASPECT;
  const maxAspect = opts.maxAspect ?? KYC_CLIENT_MAX_ASPECT;

  const warnings: string[] = [];
  // Hard blocks first (most specific retake message wins).
  if (m.blurScore < blurBlock) {
    return {
      ok: false,
      warnings,
      blockReason: 'This photo looks too blurry to read. Hold the camera steady (or rest it on a surface) and capture again so all text is sharp.',
    };
  }
  if (m.contrast < contrastBlock) {
    return {
      ok: false,
      warnings,
      blockReason: 'This photo has almost no detail (it looks flat or washed out). Re-capture in better lighting with the document filling the frame.',
    };
  }
  if (m.brightness < brightnessLow) {
    return {
      ok: false,
      warnings,
      blockReason: 'This photo is too dark to read. Move to brighter, even lighting (avoid direct glare) and capture again.',
    };
  }
  if (m.brightness > brightnessHigh) {
    return {
      ok: false,
      warnings,
      blockReason: 'This photo looks overexposed or washed out (likely glare). Move away from direct light, avoid flash, and capture again.',
    };
  }
  if (m.aspectRatio < minAspect || m.aspectRatio > maxAspect) {
    return {
      ok: false,
      warnings,
      blockReason: 'This photo has the wrong shape for a document. Hold the device so the full document (all four corners) fills the frame and capture again.',
    };
  }
  // Borderline warnings (user may proceed).
  if (m.blurScore < blurThreshold) {
    warnings.push('The photo looks slightly soft. For a faster review, re-capture with the document filling the frame and all text sharp.');
  }
  if (m.contrast < contrastLow) {
    warnings.push('The photo is a little low-contrast. Re-capture in stronger, even lighting for a faster review.');
  }
  if (m.brightness < brightnessLow + 15) {
    warnings.push('The photo is a little dark. A brighter, even-lit re-capture will speed up your review.');
  }
  return { ok: true, warnings };
}

// Deskew angle selection. Given a map of angle → row-sum-variance score, pick
// the angle with the highest score, but ONLY when it beats the baseline (angle 0)
// by at least minImprovement (fractional) AND the angle is non-trivial (|a| >= step).
// Returns 0 when deskew should not be applied. Pure.
export function selectDeskewAngle(
  scoreByAngle: Map<number, number>,
  baselineScore: number,
  opts: { minImprovement?: number; step?: number } = {},
): number {
  const minImprovement = opts.minImprovement ?? KYC_CLIENT_DESKEW_MIN_IMPROVEMENT;
  const step = opts.step ?? KYC_CLIENT_DESKEW_STEP;
  let bestAngle = 0;
  let bestScore = baselineScore;
  for (const [angle, score] of scoreByAngle) {
    if (score > bestScore) {
      bestScore = score;
      bestAngle = angle;
    }
  }
  if (bestAngle === 0) return 0;
  if (Math.abs(bestAngle) < step) return 0;
  if (baselineScore <= 0) return bestAngle;
  if (bestScore < baselineScore * (1 + minImprovement)) return 0;
  return bestAngle;
}

// Row-sum variance of "ink" (|lum - bgMean|) across rows — higher = text lines
// are more horizontally aligned. Used by the deskew search. Pure.
export function rowSumVariance(
  gray: Uint8Array | number[],
  width: number,
  height: number,
  bgMean: number,
): number {
  if (height < 2) return 0;
  const rowSums = new Float32Array(height);
  for (let y = 0; y < height; y++) {
    let s = 0;
    const off = y * width;
    for (let x = 0; x < width; x++) {
      const d = gray[off + x] - bgMean;
      s += d < 0 ? -d : d;
    }
    rowSums[y] = s;
  }
  let sum = 0;
  for (let i = 0; i < height; i++) sum += rowSums[i];
  const mean = sum / height;
  let acc = 0;
  for (let i = 0; i < height; i++) {
    const d = rowSums[i] - mean;
    acc += d * d;
  }
  return acc / height;
}

// ── Canvas-dependent helpers (integration only; no vitest — needs a real canvas) ──

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.floor(w));
  c.height = Math.max(1, Math.floor(h));
  return c;
}

// Load a File/Blob into an ImageBitmap with EXIF orientation auto-applied.
// Falls back to a plain createImageBitmap (no EXIF) if the option is unsupported.
export async function bitmapFromBlob(blob: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  } catch {
    return await createImageBitmap(blob);
  }
}

// Read a bitmap's pixels as RGBA via a canvas.
export function bitmapToRgba(bitmap: ImageBitmap | HTMLCanvasElement): {
  data: Uint8ClampedArray; width: number; height: number;
} {
  const w = bitmap.width, h = bitmap.height;
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d')!;
  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  return { data: img.data, width: w, height: h };
}

// Downscale a bitmap so its longest edge <= maxEdge (preserves aspect). No-op if already small.
export async function downscaleBitmap(bitmap: ImageBitmap, maxEdge: number): Promise<ImageBitmap> {
  const w = bitmap.width, h = bitmap.height;
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return bitmap;
  const scale = maxEdge / longest;
  const nw = Math.round(w * scale), nh = Math.round(h * scale);
  const c = makeCanvas(nw, nh);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, nw, nh);
  return await createImageBitmap(c);
}

// Crop a bitmap to the given rect (in bitmap pixels).
export async function cropBitmap(
  bitmap: ImageBitmap,
  crop: { left: number; top: number; right: number; bottom: number },
): Promise<ImageBitmap> {
  const w = crop.right - crop.left;
  const h = crop.bottom - crop.top;
  if (w <= 0 || h <= 0) return bitmap;
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d')!;
  ctx.drawImage(
    bitmap,
    crop.left, crop.top, w, h,
    0, 0, w, h,
  );
  return await createImageBitmap(c);
}

// Rotate a bitmap around its center by angleDeg (counter-clockwise positive). The
// canvas grows to fit the rotated image.
export async function rotateBitmap(bitmap: ImageBitmap, angleDeg: number): Promise<ImageBitmap> {
  if (!angleDeg) return bitmap;
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const w = bitmap.width, h = bitmap.height;
  const nw = Math.ceil(w * cos + h * sin);
  const nh = Math.ceil(h * cos + w * sin);
  const c = makeCanvas(nw, nh);
  const ctx = c.getContext('2d')!;
  ctx.translate(nw / 2, nh / 2);
  ctx.rotate(rad);
  ctx.drawImage(bitmap, -w / 2, -h / 2);
  return await createImageBitmap(c);
}

// Encode a bitmap to a JPEG blob.
export function bitmapToJpegBlob(bitmap: ImageBitmap, quality: number): Promise<Blob> {
  const c = makeCanvas(bitmap.width, bitmap.height);
  const ctx = c.getContext('2d')!;
  // White background so transparent corners (from deskew) don't go black in JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);
  return new Promise<Blob>((resolve, reject) => {
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', quality);
  });
}

// Max edge for the ink-mask + quality-assessment analysis pass. Smaller than the
// deskew-analysis thumb (which is even smaller) so the analysis is fast on a phone.
const ANALYSIS_MAX_EDGE = 1000;

// Full orchestrator. Auto-orient (EXIF) → auto-crop to the document → assess quality
// (R1) → deskew (R2) → downscale → JPEG compress. Returns the processed blob +
// the quality decision. When quality.ok === false the blob is null and the caller
// must show quality.blockReason and NOT accept the upload.
//
// `opts` is for future tunability (currently unused; thresholds are module constants).
export async function preprocessKycImage(
  file: File,
  _opts: { maxLongEdge?: number; jpegQuality?: number } = {},
): Promise<KycPreprocessResult> {
  const maxLongEdge = _opts.maxLongEdge ?? KYC_CLIENT_MAX_LONG_EDGE;
  const jpegQuality = _opts.jpegQuality ?? KYC_CLIENT_JPEG_QUALITY;

  // 1. EXIF auto-orient.
  const bitmap = await bitmapFromBlob(file);

  // 2. Auto-crop: compute ink mask + bbox on a small analysis bitmap, map back to
  //    full-res, cap the trim so a bad edge-detection can never cut the document.
  //    DISABLED by HOTFIX 2026-07-21 (KYC_CLIENT_AUTOCROP_ENABLED=false): the ink-mask
  //    bbox misidentifies document boundaries on real uploads and crops to a corner.
  //    When disabled we upload the full, EXIF-oriented image (known-good behaviour).
  let crop: { left: number; top: number; right: number; bottom: number } | null = null;
  if (KYC_CLIENT_AUTOCROP_ENABLED) {
    const analysis = await downscaleBitmap(bitmap, ANALYSIS_MAX_EDGE);
    const aRgba = bitmapToRgba(analysis);
    const aGray = toGrayscaleArray(aRgba.data, aRgba.width, aRgba.height);
    const { mask } = computeInkMask(aGray, aRgba.width, aRgba.height, KYC_CLIENT_INK_THRESHOLD);
    const marginPx = Math.floor(Math.min(aRgba.width, aRgba.height) * KYC_CLIENT_CROP_MARGIN_RATIO);
    const bbox = computeBoundingBox(mask, aRgba.width, aRgba.height, marginPx, KYC_CLIENT_CROP_MIN_AREA_RATIO);
    const scaleX = bitmap.width / aRgba.width;
    const scaleY = bitmap.height / aRgba.height;
    if (bbox) {
      const maxTrimW = bitmap.width * KYC_CLIENT_CROP_MAX_TRIM_RATIO;
      const maxTrimH = bitmap.height * KYC_CLIENT_CROP_MAX_TRIM_RATIO;
      const left = Math.max(0, Math.min(Math.floor(bbox.left * scaleX), maxTrimW));
      const top = Math.max(0, Math.min(Math.floor(bbox.top * scaleY), maxTrimH));
      const right = Math.max(bitmap.width - maxTrimW, Math.min(Math.ceil(bbox.right * scaleX), bitmap.width - maxTrimW));
      const bottom = Math.max(bitmap.height - maxTrimH, Math.min(Math.ceil(bbox.bottom * scaleY), bitmap.height - maxTrimH));
      // Skip the crop if it would remove more than half of either dimension (likely a
      // false edge-detection — don't risk cutting the document).
      if (right - left >= bitmap.width * 0.5 && bottom - top >= bitmap.height * 0.5) {
        crop = { left, top, right, bottom };
      }
    }
  }
  const croppedBitmap = crop ? await cropBitmap(bitmap, crop) : bitmap;

  // 3. R1 quality assessment on the cropped image.
  const qAnalysis = await downscaleBitmap(croppedBitmap, ANALYSIS_MAX_EDGE);
  const qRgba = bitmapToRgba(qAnalysis);
  const qGray = toGrayscaleArray(qRgba.data, qRgba.width, qRgba.height);
  const blurScore = computeLaplacianVariance(qGray, qRgba.width, qRgba.height);
  const { brightness, contrast } = computeBrightnessContrast(qGray);
  const metrics: KycImageMetrics = {
    width: croppedBitmap.width,
    height: croppedBitmap.height,
    blurScore,
    brightness,
    contrast,
    aspectRatio: croppedBitmap.width / croppedBitmap.height,
  };
  const quality = decideQualityFromMetrics(metrics);
  if (!quality.ok) {
    return {
      blob: null, previewUrl: '',
      width: metrics.width, height: metrics.height,
      metrics, quality,
      cropped: !!crop, deskewed: false, deskewAngle: 0,
    };
  }

  // 4. R2 deskew: search a small angle range on a small thumb, pick the angle that
  //    maximises row-sum variance (text lines become more horizontal). The angle
  //    is only applied when it beats the baseline by >= 10% — so a straight
  //    document is never made crooked.
  //    DISABLED by HOTFIX 2026-07-21 (KYC_CLIENT_DESKEW_ENABLED=false): deskew
  //    depends on the (now disabled) crop and can introduce tilt. GPT-4o handles
  //    mild tilt fine; we upload the un-rotated image until deskew is revalidated.
  let finalBitmap = croppedBitmap;
  let deskewed = false;
  let deskewAngle = 0;
  if (KYC_CLIENT_DESKEW_ENABLED) {
    const dAnalysis = await downscaleBitmap(croppedBitmap, KYC_CLIENT_DESKEW_ANALYSIS_MAX_EDGE);
    const dRgba = bitmapToRgba(dAnalysis);
    const dGray = toGrayscaleArray(dRgba.data, dRgba.width, dRgba.height);
    const dBg = computeInkMask(dGray, dRgba.width, dRgba.height, KYC_CLIENT_INK_THRESHOLD).bgMean;
    const baseline = rowSumVariance(dGray, dRgba.width, dRgba.height, dBg);
    const scores = new Map<number, number>();
    scores.set(0, baseline);
    for (let a = -KYC_CLIENT_DESKEW_MAX_ANGLE; a <= KYC_CLIENT_DESKEW_MAX_ANGLE; a += KYC_CLIENT_DESKEW_STEP) {
      if (a === 0) continue;
      const rot = await rotateBitmap(dAnalysis, a);
      const rRgba = bitmapToRgba(rot);
      const rGray = toGrayscaleArray(rRgba.data, rRgba.width, rRgba.height);
      scores.set(a, rowSumVariance(rGray, rRgba.width, rRgba.height, dBg));
    }
    const angle = selectDeskewAngle(scores, baseline);
    if (angle !== 0) {
      finalBitmap = await rotateBitmap(croppedBitmap, angle);
      deskewed = true;
      deskewAngle = angle;
    }
  }

  // 5. Downscale to the upload cap + JPEG compress.
  const out = await downscaleBitmap(finalBitmap, maxLongEdge);
  const blob = await bitmapToJpegBlob(out, jpegQuality);
  const previewUrl = URL.createObjectURL(blob);

  return {
    blob, previewUrl,
    width: out.width, height: out.height,
    metrics, quality,
    cropped: !!crop, deskewed, deskewAngle,
  };
}
